/**
 * Event detail modal: view, reschedule, assign and delete one visit.
 *
 * Lives on #eventModal (templates/calendar/_event_modal.html): the calendar
 * opens it from a FullCalendar event, the Dispatch board from a card, a quote
 * or job page from its Scheduled Events card. Every opener dispatches
 * `event-detail:open` through openEventDetail() (static/js/calendar/event-detail.js) with:
 *   event      { id, title, start: Date, end: Date|null, allDay, extendedProps }
 *              the calendar feed's shape (CalendarEvent.to_fullcalendar_dict)
 *   onChanged  ({ kind: 'rescheduled'|'assigned'|'deleted', id }) => void
 * The modal owns the write; the opener owns what it repaints, because only the
 * opener knows whether that is a FullCalendar feed or a server-rendered page.
 *
 * Reschedule is the schedule modal's job (schedule_modal_controller.js): its
 * calendar is where a day and time are picked, so this modal hands the visit
 * over rather than keeping a second, smaller date form. Assign sends the crew
 * only. The Assign panel is absent for someone without assign_work.
 */
import { Controller } from '@hotwired/stimulus';

import { crewFromAssignees, readAssigneeSelection, seedAssigneePicker } from '../../utils/assignee-selection.js';
import { describeWhen } from './_visit_time.js';

export default class extends Controller {
    static targets = [
        'details', 'title', 'type', 'when', 'location', 'assignees', 'linkRow', 'link', 'linkText',
        'assign', 'detailsFooter', 'assignFooter', 'deleteButton', 'assignButton',
    ];
    static values = { url: String };

    open({ detail }) {
        this._event = detail.event;
        this._onChanged = detail.onChanged;
        this._fillDetails(detail.event);
        this.showDetails();
        window.bootstrap.Modal.getOrCreateInstance(this.element).show();
    }

    showDetails() {
        this._showPanel('details');
    }

    // Hand the visit to the schedule modal once this one has closed (two
    // Bootstrap modals must not be open at once). That modal announces a save
    // with `schedule:changed` on the document, which reaches the opener as a
    // reschedule; the listener goes when it fires or when the schedule modal
    // next opens for anything else.
    reschedule() {
        const scheduleModal = document.getElementById('scheduleEventModal');
        if (!this._event || !scheduleModal) return;
        const visit = this._event;
        const onChanged = this._onChanged;
        const onSaved = (event) => {
            if (event.detail?.action !== 'reschedule') return;
            stopListening();
            onChanged({ kind: 'rescheduled', id: visit.id });
        };
        const stopListening = () => document.removeEventListener('schedule:changed', onSaved);

        this.element.addEventListener('hidden.bs.modal', () => {
            document.addEventListener('schedule:changed', onSaved);
            scheduleModal.dispatchEvent(new CustomEvent('schedule-modal:reschedule', { detail: { visit } }));
            scheduleModal.addEventListener('show.bs.modal', stopListening, { once: true });
        }, { once: true });
        window.bootstrap.Modal.getOrCreateInstance(this.element).hide();
        this._event = null;
    }

    showAssign() {
        if (!this._event) return;
        seedAssigneePicker(this.element, crewFromAssignees(this._event.extendedProps.assignees));
        this._showPanel('assign');
    }

    async confirmAssign() {
        const eventId = this._event.id;
        const ok = await this._write(this.assignButtonTarget, 'Saving...', 'PUT',
            readAssigneeSelection(this.element), 'Failed to update the crew. Please try again.');
        if (ok) this._finish({ kind: 'assigned', id: eventId }, 'Crew updated');
    }

    async delete() {
        if (!this._event || this._deleting) return;
        if (!confirm('Are you sure you want to delete this event?')) return;
        this._deleting = true;
        const eventId = this._event.id;
        try {
            const ok = await this._write(this.deleteButtonTarget, 'Deleting...', 'DELETE', null,
                'Failed to delete event. Please try again.');
            if (ok) this._finish({ kind: 'deleted', id: eventId }, 'Event deleted successfully');
        } finally {
            this._deleting = false;
        }
    }

    /* ------------------------------------------------------------- private */

    _fillDetails(event) {
        const type = event.extendedProps.type || 'other';
        this.titleTarget.textContent = event.title;
        this.typeTarget.textContent = type.charAt(0).toUpperCase() + type.slice(1);
        this.whenTarget.textContent = describeWhen(event.start, event.end, event.allDay);
        // The stored location can carry <br> line breaks from the address block.
        this.locationTarget.textContent = (event.extendedProps.location || 'Not specified')
            .replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '');
        // The visit's crew by name, as the Scheduled Events card lists it.
        const crew = event.extendedProps.assignees || [];
        this.assigneesTarget.textContent = crew.map((a) => a.name).join(', ') || 'Unassigned';

        const { job_id: jobId, quote_id: quoteId } = event.extendedProps;
        this.linkRowTarget.hidden = !(jobId || quoteId);
        if (jobId) {
            this.linkTarget.href = `/job/${jobId}`;
            this.linkTextTarget.textContent = 'View Job';
        } else if (quoteId) {
            this.linkTarget.href = `/quote/${quoteId}`;
            this.linkTextTarget.textContent = 'View Quote';
        }
    }

    // Show one panel ('details' | 'assign') and its footer.
    _showPanel(name) {
        this.detailsTarget.hidden = name !== 'details';
        this.detailsFooterTarget.hidden = name !== 'details';
        if (this.hasAssignTarget) {
            this.assignTarget.hidden = name !== 'assign';
            this.assignFooterTarget.hidden = name !== 'assign';
        }
    }

    // Send one write with the button showing progress. Resolves true on success;
    // on failure tells the user and resolves false.
    async _write(button, busyLabel, method, body, fallbackError) {
        const label = button.innerHTML;
        button.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${busyLabel}`;
        button.disabled = true;
        try {
            const response = await fetch(this.urlValue.replace(/0$/, this._event.id), {
                method,
                headers: { 'Content-Type': 'application/json' },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
            if (response.ok) return true;
            const data = await response.json().catch(() => ({}));
            notify(data.error || fallbackError, 'error');
            return false;
        } catch (error) {
            console.error('Event write failed:', error);
            notify(fallbackError, 'error');
            return false;
        } finally {
            button.innerHTML = label;
            button.disabled = false;
        }
    }

    _finish(change, message) {
        window.bootstrap.Modal.getOrCreateInstance(this.element).hide();
        this._event = null;
        notify(message, 'success');
        this._onChanged(change);
    }
}

function notify(message, level) {
    if (typeof window.showNotification === 'function') window.showNotification(message, level);
}
